// src/Views/ProfileView.js
import React, { useState, useContext, useEffect, useRef } from 'react';
import AuthService from '../services/AuthService';
import { AuthContext } from '../contexts/AuthContext';
import LogInOrSignUpView from './LogInOrSignUpView';
import {
  MAX_CONTENT_WIDTH,
  BORDER_RADIUS,
  SPACING_TIGHT,
  SPACING_ELEMENT,
  SPACING_COMPACT,
  SPACING_COMPONENT,
  SPACING_BUTTON,
  SPACING_CARD,
  SPACING_CARD_BODY,
  SPACING_GROUP,
  SPACING_SECTION,
  SPACING_HERO,
  SPACING_PAGE,
} from '../constants/layout';
import {
  FONT_TITLE,
  FONT_TITLE2,
  FONT_TITLE2_MOBILE,
  FONT_TITLE3,
  FONT_BODY,
  FONT_FOOTNOTE,
} from '../constants/typography';

function ProfileView() {
  const { user, loading } = useContext(AuthContext);
  const [showAuth, setShowAuth] = useState(false);
  const [error, setError] = useState('');
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoError, setPhotoError] = useState('');
  const fileInputRef = useRef(null);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  // Fetch profile data from Firestore when user changes
  useEffect(() => {
    const fetchProfile = async () => {
      if (user) {
        try {
          const profile = await AuthService.getPublicProfile();
          setProfileData(profile);
        } catch (err) {
          console.error('Error fetching profile:', err);
        }
      } else {
        setProfileData(null);
      }
    };

    fetchProfile();
  }, [user]);

  const handleGoogleSignIn = async () => {
    setIsGoogleLoading(true);
    setError('');

    try {
      await AuthService.signInWithGoogle();
    } catch (err) {
      // Check if user canceled
      if (err.code === 'auth/popup-closed-by-user' || err.code === 'auth/cancelled-popup-request') {
        return;
      }
      setError(err.message);
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await AuthService.signOut();
    } catch (err) {
      setError(err.message);
    }
  };

  const handlePhotoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingPhoto(true);
    setPhotoError('');

    try {
      const newPhotoURL = await AuthService.uploadProfilePhoto(file);

      // Update local profile data
      setProfileData(prev => ({
        ...prev,
        profilePhotoURL: newPhotoURL,
      }));
    } catch (err) {
      setPhotoError(err.message);
    } finally {
      setUploadingPhoto(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeletePhoto = async () => {
    if (!window.confirm('Are you sure you want to delete your profile photo?')) {
      return;
    }

    setUploadingPhoto(true);
    setPhotoError('');

    try {
      await AuthService.deleteProfilePhoto();

      // Update local profile data
      setProfileData(prev => ({
        ...prev,
        profilePhotoURL: null,
      }));
    } catch (err) {
      setPhotoError(err.message);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleEditProfile = () => {
    // Pre-fill the form with current data
    setEditFirstName(profileData?.firstName || '');
    setEditLastName(profileData?.lastName || '');
    setSaveError('');
    setPhotoError('');
    setShowEditProfile(true);
  };

  const handleCloseEditModal = () => {
    setShowEditProfile(false);
    setSaveError('');
    setPhotoError('');
  };

  const handleSaveProfile = async () => {
    if (!editFirstName.trim()) {
      setSaveError('First name is required');
      return;
    }

    setIsSaving(true);
    setSaveError('');

    try {
      const trimmedLastName = editLastName.trim();
      await AuthService.updateUserProfile(
        editFirstName.trim(),
        trimmedLastName || null,
        true // improveModelForEveryone
      );

      // Update local profile data
      setProfileData(prev => ({
        ...prev,
        firstName: editFirstName.trim(),
        lastName: trimmedLastName || null,
      }));

      handleCloseEditModal();
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="profile-container loading">
        <p style={{ color: 'var(--color-text-secondary)', textAlign: 'center' }}>Loading...</p>

        <style jsx>{`
          .profile-container {
            max-width: ${MAX_CONTENT_WIDTH};
            margin: 0 auto;
            padding: 0 ${SPACING_CARD} ${SPACING_HERO};
          }

          .profile-container.loading {
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 400px;
          }
        `}</style>
      </div>
    );
  }

  if (user) {
    // User is signed in - show profile
    // Use profileData from Firestore if available, otherwise fall back to Firebase Auth user
    const displayName =
      profileData?.firstName && profileData?.lastName
        ? `${profileData.firstName} ${profileData.lastName}`
        : profileData?.firstName || user.displayName || '';

    const memberSince = user.metadata?.creationTime
      ? new Date(user.metadata.creationTime).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        })
      : null;

    // Use profilePhotoURL from Firestore profile, fall back to Firebase Auth photoURL
    const profilePhotoURL = profileData?.profilePhotoURL || user.photoURL;

    return (
      <div className="profile-container">
        <div className="profile-header">
          <h1 className="section-title">Profile</h1>
        </div>

        {/* Profile Image Section */}
        <div className="profile-image-section">
          <div className="profile-image-wrapper">
            {profilePhotoURL ? (
              <img src={profilePhotoURL} alt="Profile" className="profile-image" />
            ) : (
              <div className="profile-image-placeholder">
                <span className="material-icons">account_circle</span>
              </div>
            )}
          </div>
        </div>

        <div className="profile-info">
          <div className="profile-info-header">
            <h2 className="info-section-title">Personal Information</h2>
            <button onClick={handleEditProfile} className="edit-button">
              <span className="material-icons">edit</span>
              Edit
            </button>
          </div>

          {displayName && (
            <div className="info-row">
              <span className="info-label">Name</span>
              <span className="info-value">{displayName}</span>
            </div>
          )}

          <div className="info-row">
            <span className="info-label">Email</span>
            <span className="info-value">{user.email}</span>
          </div>

          {memberSince && (
            <div className="info-row">
              <span className="info-label">Member Since</span>
              <span className="info-value">{memberSince}</span>
            </div>
          )}
        </div>

        {/* Account Management Notice */}
        <div className="account-notice">
          <div className="notice-header">
            <span className="material-icons notice-icon">info</span>
            <h3 className="notice-title">Account Management</h3>
          </div>
          <p className="notice-text">
            To change your email, password, or delete your account, please use the iOS app.
          </p>
        </div>

        {/* Log Out Section */}
        <div className="logout-section">
          <button onClick={handleSignOut} className="logout-button">
            Log Out
          </button>
        </div>

        {/* Edit Profile Modal */}
        {showEditProfile && (
          <div className="modal-overlay" onClick={handleCloseEditModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2 className="modal-title">Edit Profile</h2>
                <button
                  onClick={handleCloseEditModal}
                  className="modal-close"
                  aria-label="Close"
                >
                  <span className="material-icons">close</span>
                </button>
              </div>

              <div className="modal-body">
                {/* Profile Photo Section */}
                <div className="modal-photo-section">
                  <div className="modal-photo-wrapper">
                    {profilePhotoURL ? (
                      <img src={profilePhotoURL} alt="Profile" className="modal-profile-image" />
                    ) : (
                      <div className="modal-profile-placeholder">
                        <span className="material-icons">account_circle</span>
                      </div>
                    )}
                    {uploadingPhoto && (
                      <div className="modal-photo-overlay">
                        <div className="spinner"></div>
                      </div>
                    )}
                  </div>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoSelect}
                    style={{ display: 'none' }}
                  />

                  <div className="modal-photo-actions">
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="modal-photo-button"
                      disabled={uploadingPhoto || isSaving}
                      type="button"
                    >
                      {profilePhotoURL ? 'Change Photo' : 'Upload Photo'}
                    </button>
                    {profilePhotoURL && (
                      <button
                        onClick={handleDeletePhoto}
                        className="modal-photo-button modal-photo-delete"
                        disabled={uploadingPhoto || isSaving}
                        type="button"
                      >
                        Delete Photo
                      </button>
                    )}
                  </div>

                  {photoError && <p className="modal-photo-error">{photoError}</p>}
                </div>

                <div className="form-group">
                  <label htmlFor="firstName" className="form-label">
                    First Name <span className="required">*</span>
                  </label>
                  <input
                    id="firstName"
                    type="text"
                    value={editFirstName}
                    onChange={(e) => setEditFirstName(e.target.value)}
                    className="form-input"
                    placeholder="Required"
                    autoComplete="given-name"
                    disabled={isSaving}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="lastName" className="form-label">
                    Last Name
                  </label>
                  <input
                    id="lastName"
                    type="text"
                    value={editLastName}
                    onChange={(e) => setEditLastName(e.target.value)}
                    className="form-input"
                    placeholder="Optional"
                    autoComplete="family-name"
                    disabled={isSaving}
                  />
                </div>

                {saveError && <p className="save-error">{saveError}</p>}
              </div>

              <div className="modal-footer">
                <button
                  onClick={handleCloseEditModal}
                  className="cancel-button"
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveProfile}
                  className="save-button"
                  disabled={isSaving || !editFirstName.trim()}
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}

        <style jsx>{`
          .profile-container {
            max-width: ${MAX_CONTENT_WIDTH};
            margin: 0 auto;
            padding: ${SPACING_HERO} ${SPACING_CARD} ${SPACING_PAGE};
          }

          .profile-header {
            margin-bottom: ${SPACING_SECTION};
          }

          .section-title {
            font-size: ${FONT_TITLE2};
            font-family: var(--font-family-sans);
            font-weight: 800;
            margin: 0;
            text-transform: uppercase;
            color: var(--color-text-primary);
          }

          /* Profile Image Section */
          .profile-image-section {
            display: flex;
            justify-content: center;
            margin-bottom: ${SPACING_SECTION};
          }

          .profile-image-wrapper {
            width: 100px;
            height: 100px;
          }

          .profile-image {
            width: 100px;
            height: 100px;
            border-radius: 50%;
            object-fit: cover;
            border: 2px solid var(--color-border);
            display: block;
          }

          .profile-image-placeholder {
            width: 100px;
            height: 100px;
            border-radius: 50%;
            background-color: var(--color-surface-muted);
            display: flex;
            align-items: center;
            justify-content: center;
            color: var(--color-text-secondary);
          }

          .profile-image-placeholder .material-icons {
            font-size: 64px;
          }

          .profile-info {
            margin-bottom: ${SPACING_GROUP};
          }

          .profile-info-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: ${SPACING_CARD};
          }

          .info-section-title {
            font-size: ${FONT_BODY};
            font-weight: 600;
            margin: 0;
            color: var(--color-text-primary);
          }

          .edit-button {
            display: flex;
            align-items: center;
            gap: ${SPACING_TIGHT};
            padding: ${SPACING_ELEMENT} ${SPACING_BUTTON};
            background-color: transparent;
            color: var(--color-accent);
            border: 1px solid var(--color-accent);
            border-radius: ${BORDER_RADIUS};
            font-size: ${FONT_FOOTNOTE};
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s ease;
          }

          .edit-button .material-icons {
            font-size: 16px;
          }

          .edit-button:hover {
            background-color: var(--color-accent);
            color: var(--color-accent-text);
          }

          .info-row {
            padding: ${SPACING_CARD} 0;
            border-bottom: 1px solid var(--color-border);
            display: flex;
            justify-content: space-between;
            align-items: baseline;
            gap: ${SPACING_CARD};
          }

          .info-row:last-child {
            border-bottom: none;
          }

          .info-label {
            font-size: ${FONT_FOOTNOTE};
            color: var(--color-text-secondary);
            flex-shrink: 0;
          }

          .info-value {
            font-size: ${FONT_BODY};
            color: var(--color-text-primary);
            font-weight: 500;
            text-align: right;
          }

          /* Account Management Notice */
          .account-notice {
            background-color: var(--color-surface-muted);
            border: 1px solid var(--color-border);
            border-radius: ${BORDER_RADIUS};
            padding: ${SPACING_CARD};
            margin-top: ${SPACING_GROUP};
          }

          .notice-header {
            display: flex;
            align-items: center;
            gap: ${SPACING_COMPACT};
            margin-bottom: ${SPACING_COMPACT};
          }

          .notice-icon {
            font-size: 20px;
            color: var(--color-text-secondary);
          }

          .notice-title {
            font-size: ${FONT_BODY};
            font-weight: 600;
            margin: 0;
            color: var(--color-text-primary);
          }

          .notice-text {
            font-size: ${FONT_FOOTNOTE};
            color: var(--color-text-secondary);
            margin: 0;
            line-height: 1.5;
          }

          /* Log Out Section */
          .logout-section {
            margin-top: ${SPACING_SECTION};
          }

          .logout-button {
            width: 100%;
            padding: ${SPACING_BUTTON} ${SPACING_CARD_BODY};
            background-color: var(--color-accent);
            color: var(--color-accent-text);
            border: none;
            border-radius: ${BORDER_RADIUS};
            font-size: ${FONT_BODY};
            font-weight: 600;
            cursor: pointer;
            transition: background-color 0.2s ease;
          }

          .logout-button:hover {
            background-color: var(--color-accent-hover);
          }

          @media (max-width: 600px) {
            .section-title {
              font-size: ${FONT_TITLE2_MOBILE};
            }

            .info-row {
              flex-direction: column;
              align-items: flex-start;
              gap: ${SPACING_TIGHT};
            }

            .info-value {
              text-align: left;
            }
          }

          /* Modal Styles */
          .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
            padding: ${SPACING_CARD};
          }

          .modal-content {
            background-color: var(--color-background);
            border-radius: ${BORDER_RADIUS};
            max-width: 500px;
            width: 100%;
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
          }

          .modal-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: ${SPACING_CARD_BODY} ${SPACING_GROUP};
            border-bottom: 1px solid var(--color-border);
          }

          .modal-title {
            font-size: ${FONT_TITLE3};
            font-weight: 600;
            margin: 0;
            color: var(--color-text-primary);
          }

          .modal-close {
            background: none;
            border: none;
            cursor: pointer;
            padding: ${SPACING_TIGHT};
            color: var(--color-text-secondary);
            transition: color 0.2s ease;
          }

          .modal-close:hover {
            color: var(--color-text-primary);
          }

          .modal-close .material-icons {
            font-size: 24px;
          }

          .modal-body {
            padding: ${SPACING_GROUP};
          }

          .modal-photo-section {
            display: flex;
            flex-direction: column;
            align-items: center;
            margin-bottom: ${SPACING_GROUP};
            padding-bottom: ${SPACING_GROUP};
            border-bottom: 1px solid var(--color-border);
          }

          .modal-photo-wrapper {
            position: relative;
            width: 100px;
            height: 100px;
            margin-bottom: ${SPACING_CARD};
          }

          .modal-profile-image {
            width: 100px;
            height: 100px;
            border-radius: 50%;
            object-fit: cover;
            border: 2px solid var(--color-border);
            display: block;
          }

          .modal-profile-placeholder {
            width: 100px;
            height: 100px;
            border-radius: 50%;
            background-color: var(--color-surface-muted);
            display: flex;
            align-items: center;
            justify-content: center;
            color: var(--color-text-secondary);
          }

          .modal-profile-placeholder .material-icons {
            font-size: 64px;
          }

          .modal-photo-overlay {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background-color: rgba(0, 0, 0, 0.5);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .modal-photo-overlay .spinner {
            width: 32px;
            height: 32px;
            border: 3px solid rgba(255, 255, 255, 0.3);
            border-top-color: white;
            border-radius: 50%;
            animation: spin 0.6s linear infinite;
          }

          @keyframes spin {
            to {
              transform: rotate(360deg);
            }
          }

          .modal-photo-actions {
            display: flex;
            gap: ${SPACING_BUTTON};
            justify-content: center;
          }

          .modal-photo-button {
            padding: ${SPACING_COMPACT} ${SPACING_CARD};
            background-color: var(--color-accent);
            color: var(--color-accent-text);
            border: none;
            border-radius: ${BORDER_RADIUS};
            font-size: ${FONT_FOOTNOTE};
            font-weight: 600;
            cursor: pointer;
            transition: background-color 0.2s ease;
          }

          .modal-photo-button:hover:not(:disabled) {
            background-color: var(--color-accent-hover);
          }

          .modal-photo-button:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }

          .modal-photo-button.modal-photo-delete {
            background-color: #ef4444;
            color: white;
          }

          .modal-photo-button.modal-photo-delete:hover:not(:disabled) {
            background-color: #dc2626;
          }

          .modal-photo-error {
            color: #ef4444;
            font-size: ${FONT_FOOTNOTE};
            margin-top: ${SPACING_BUTTON};
            text-align: center;
          }

          .form-group {
            margin-bottom: ${SPACING_CARD_BODY};
          }

          .form-group:last-child {
            margin-bottom: 0;
          }

          .form-label {
            display: block;
            font-size: ${FONT_FOOTNOTE};
            font-weight: 600;
            color: var(--color-text-primary);
            margin-bottom: ${SPACING_COMPACT};
          }

          .required {
            color: #ef4444;
          }

          .form-input {
            width: 100%;
            padding: ${SPACING_BUTTON};
            border: 1px solid var(--color-border);
            border-radius: ${BORDER_RADIUS};
            font-size: ${FONT_BODY};
            color: var(--color-text-primary);
            background-color: var(--color-surface);
            transition: border-color 0.2s ease;
          }

          .form-input:focus {
            outline: none;
            border-color: var(--color-accent);
          }

          .form-input:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }

          .save-error {
            color: #ef4444;
            font-size: ${FONT_FOOTNOTE};
            margin: ${SPACING_CARD} 0 0;
          }

          .modal-footer {
            display: flex;
            justify-content: flex-end;
            gap: ${SPACING_BUTTON};
            padding: ${SPACING_CARD_BODY} ${SPACING_GROUP};
            border-top: 1px solid var(--color-border);
          }

          .cancel-button,
          .save-button {
            padding: ${SPACING_COMPONENT} ${SPACING_CARD_BODY};
            border: none;
            border-radius: ${BORDER_RADIUS};
            font-size: ${FONT_BODY};
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s ease;
          }

          .cancel-button {
            background-color: transparent;
            color: var(--color-text-secondary);
            border: 1px solid var(--color-border);
          }

          .cancel-button:hover:not(:disabled) {
            background-color: var(--color-surface-muted);
          }

          .save-button {
            background-color: var(--color-accent);
            color: var(--color-accent-text);
          }

          .save-button:hover:not(:disabled) {
            background-color: var(--color-accent-hover);
          }

          .save-button:disabled,
          .cancel-button:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }
        `}</style>
      </div>
    );
  }

  // User is NOT signed in - show sign-in prompt
  return (
    <>
      <div className="profile-container">
        <div className="sign-in-prompt">
          <div className="prompt-icon">
            <span className="material-icons">account_circle</span>
          </div>
          <h2 className="prompt-title">Sign In to Your Account</h2>
          <p className="prompt-description">
            Create an account or sign in to access your orders, favorites, and more.
          </p>

          <div className="auth-buttons">
            <button
              onClick={handleGoogleSignIn}
              disabled={isGoogleLoading}
              className="auth-button google-button"
            >
              {isGoogleLoading ? (
                <span className="button-loader"></span>
              ) : (
                <>
                  <img
                    src="/google-icon.svg"
                    alt="Google"
                    className="google-icon"
                  />
                  <span>Continue with Google</span>
                </>
              )}
            </button>

            <button
              onClick={() => console.log('Apple Sign In not yet implemented')}
              className="auth-button apple-button"
            >
              <img
                src="/apple-icon.svg"
                alt="Apple"
                className="apple-icon"
              />
              <span>Continue with Apple</span>
            </button>

            <button onClick={() => setShowAuth(true)} className="auth-button primary-button">
              Sign In with Email
            </button>
          </div>

          {error && <p className="error-message">{error}</p>}
        </div>

        <style jsx>{`
          .profile-container {
            max-width: ${MAX_CONTENT_WIDTH};
            margin: 0 auto;
            padding: 0 ${SPACING_CARD};
            min-height: calc(100vh - 200px);
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .sign-in-prompt {
            max-width: 500px;
            margin: 0 auto;
            text-align: center;
            padding: ${SPACING_GROUP};
          }

          .prompt-icon {
            font-size: 80px;
            color: var(--color-text-secondary);
            opacity: 0.5;
            margin-bottom: ${SPACING_GROUP};
          }

          .prompt-icon .material-icons {
            font-size: 80px;
          }

          .prompt-title {
            font-size: ${FONT_TITLE};
            font-weight: 700;
            margin: 0 0 ${SPACING_BUTTON} 0;
            color: var(--color-text-primary);
          }

          .prompt-description {
            font-size: ${FONT_TITLE3};
            color: var(--color-text-secondary);
            margin: 0 0 ${SPACING_SECTION} 0;
            line-height: 1.5;
          }

          .auth-buttons {
            display: flex;
            flex-direction: column;
            gap: ${SPACING_BUTTON};
            max-width: 400px;
            margin: 0 auto;
          }

          .auth-button {
            height: 54px;
            border-radius: ${BORDER_RADIUS};
            border: none;
            font-size: ${FONT_TITLE3};
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s ease;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: ${SPACING_BUTTON};
          }

          .auth-button:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }

          .google-button {
            background-color: #ffffff;
            color: #3c4043;
            border: 1px solid #dadce0;
          }

          .google-button:hover:not(:disabled) {
            background-color: #f8f9fa;
            transform: translateY(-1px);
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          }

          .google-icon {
            width: 18px;
            height: 18px;
          }

          .apple-button {
            background-color: #ffffff;
            color: #000000;
            border: 1px solid #dadce0;
          }

          .apple-button:hover {
            background-color: #f8f9fa;
            transform: translateY(-1px);
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          }

          .apple-icon {
            width: 20px;
            height: 20px;
          }

          .primary-button {
            background-color: var(--color-accent);
            color: var(--color-accent-text);
          }

          .primary-button:hover {
            background-color: var(--color-accent-hover);
            transform: translateY(-1px);
          }

          .button-loader {
            width: 20px;
            height: 20px;
            border: 2px solid rgba(0, 0, 0, 0.1);
            border-top-color: #1f2937;
            border-radius: 50%;
            animation: spin 0.6s linear infinite;
          }

          @keyframes spin {
            to {
              transform: rotate(360deg);
            }
          }

          .error-message {
            color: #ef4444;
            font-size: ${FONT_FOOTNOTE};
            margin: ${SPACING_CARD} 0 0 0;
          }

          @media (max-width: 600px) {
            .section-title {
              font-size: ${FONT_TITLE2_MOBILE};
            }
          }
        `}</style>
      </div>

      {showAuth && (
        <LogInOrSignUpView
          onClose={() => setShowAuth(false)}
          onGoogleSignInRequested={handleGoogleSignIn}
        />
      )}
    </>
  );
}

export default ProfileView;
